import { useGtm } from "@gtm-support/vue-gtm";
import type { IGTMCartEvent, IGTMCartItem } from "./gtm";
import type { LineItemTypeExtension, MoneyType } from "@/core/api/graphql/types";

/**
 * Helper function to prepare GTM items from line items.
 * Shared by both add-to-cart and remove-from-cart events.
 */
const prepareLineItemsData = (lineItems: LineItemTypeExtension[]): IGTMCartItem[] => {
  const gtmItems: IGTMCartItem[] = [];

  for (const lineItem of lineItems) {
    const price: MoneyType = lineItem?.placedPrice;
    const usdPrice =
      price?.currency?.code === "USD"
        ? price
        : lineItem.product?.allCurrenciesPricesTL.find((p) => p.list.currency.code === "USD")?.list;

    const categoryBreadcrumbsDescriptionArray = JSON.parse(
      lineItem.product?.descriptionsWithFallbacksTL.find((d) => d.reviewType === "Breadcrumb")?.content || "[]",
    ) as string[][];
    let categoryBreadcrumbs = categoryBreadcrumbsDescriptionArray.map((c) => c[0]);

    //Cut out 'Main Visual Navigation' from breadcrumbs and reverse array
    categoryBreadcrumbs = categoryBreadcrumbs.length > 1 ? categoryBreadcrumbs.slice(0, -1).reverse() : [];

    const titleFromDescriptions = lineItem.product?.descriptionsWithFallbacksTL.find(
      (d) => d.reviewType === "Title",
    )?.content;

    const gtmItem: IGTMCartItem = {
      item_id: lineItem.product?.code,
      item_name: titleFromDescriptions,
      item_category: categoryBreadcrumbs.at(0),
      item_category2: categoryBreadcrumbs.at(1),
      item_category3: categoryBreadcrumbs.at(2),
      item_category4: categoryBreadcrumbs.at(3),
      item_category5: categoryBreadcrumbs.at(4),
      item_category6: categoryBreadcrumbs.at(5),
      item_category7: categoryBreadcrumbs.at(6),
      price: usdPrice?.amount ?? NaN,
      quantity: lineItem.quantity,
    };

    //Add real currency and price if it is not USD
    if (price?.currency?.code !== "USD") {
      gtmItem.foreignCurrencyCode = price?.currency?.code;
      gtmItem.foreignCurrencyAmount = price?.amount;
    }

    gtmItems.push(gtmItem);
  }

  return gtmItems;
};

/**
 * Helper function to prepare and send a GTM cart event.
 * Handles event data preparation and foreign currency conversion.
 */
const sendCartEvent = (eventName: string, lineItems: LineItemTypeExtension[]): void => {
  const gtm = useGtm();

  if (!gtm || !lineItems || lineItems.length === 0) {
    return;
  }

  const gtmItems = prepareLineItemsData(lineItems);

  const eventData: IGTMCartEvent = {
    event: eventName,
    ecommerce: {
      items: gtmItems,
      currency: "USD",
      value: gtmItems.reduce((sum, item) => sum + (item.price ?? 0) * (item.quantity ?? 0), 0),
    },
  };

  if (lineItems[0]?.placedPrice?.currency?.code !== "USD") {
    eventData.foreignCurrencyCode = lineItems[0]?.placedPrice?.currency?.code;
    eventData.foreignCurrencyAmount = gtmItems.reduce(
      (sum, item) => sum + (item.foreignCurrencyAmount ?? 0) * (item.quantity ?? 0),
      0,
    );
  }

  gtm.push(eventData);
};

/**
 * Standalone function to send GTM add to cart event for specific line items.
 * This function doesn't depend on cart state and can be used to avoid circular dependencies.
 */
export const gtmSendAddToCartEvent = (lineItems: LineItemTypeExtension[]): void => {
  sendCartEvent("add_to_cart", lineItems);
};

/**
 * Standalone function to send GTM remove from cart event for specific line items.
 * This function doesn't depend on cart state and can be used to avoid circular dependencies.
 */
export const gtmSendRemoveFromCartEvent = (lineItems: LineItemTypeExtension[]): void => {
  sendCartEvent("remove_from_cart", lineItems);
};

/**
 * Standalone function to send GTM retrieve cart event for specific line items.
 */
export const gtmSendRetrieveCartEvent = (lineItems: LineItemTypeExtension[]): void => {
  sendCartEvent("retrieve_cart", lineItems);
};
